import React, { useState, useRef, useEffect } from 'react';
import {
  Button,
  Grid2 as Grid,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  Box,
  Switch,
  FormControl,
  FormControlLabel,
} from '@mui/material';
import {
  Facebook as FacebookIcon,
  LinkedIn as LinkedInIcon,
  QrCode2 as QrCode2Icon,
} from '@mui/icons-material';
import XIcon from '@mui/icons-material/X';
import { FormattedMessage, useIntl } from 'react-intl';
import QRCodeStyling from 'qr-code-styling';
import { styled } from '@mui/material/styles';

import IconButton from '../../../DesignSystem/IconButton';
import Dialog from '../../../DesignSystem/Dialog';
import {
  shareFacebook,
  shareLinkedIn,
  shareX,
  generateQrCode,
} from '../../../../Utils/SocialShare';
import { Icons } from '../../../../Themes';
import Analytics from '../../../../Services/AnalyticsService';
import useWidth from '../../../../Hooks/useWidth';
import useSpace from '../../../../Hooks/useSpace';
import CustomIcon from '../../../DesignSystem/CustomIcon';

const StyledIconButton = styled(IconButton)(({ theme }: any) => ({
  fontSize: 0,
  padding: 0,
  border: `1px solid ${theme.palette.primary.main}`,
  height: theme.spacing(4.5),
  width: theme.spacing(4.5),
}));

interface ShareItProps {
  postId: number | string;
  postTitle: string | null;
  postType: string;
}

function ShareIt({ postId, postTitle, postType }: ShareItProps) {
  const { space } = useSpace();
  const intl = useIntl();
  const lang = intl.locale;
  const isSmallScreenSize = useWidth('down', 'sm');
  const [shareMenuAnchorEl, setShareMenuAnchorEl] = useState(null);
  const [qrCodeDialogOpen, setQrCodeDialogOpen] = useState(false);
  const [isBlackAndWhite, setIsBlackAndWhite] = useState(false);
  const qrCodeRef = useRef(null);

  const trackShareAction = (Channel: any) => {
    Analytics.track('Post', 'Share Content', `Shared post via ${Channel}`, {
      'Post ID': postId,
      'Post Type': postType,
    });
  };

  const onClickShareFacebook = () => {
    shareFacebook(lang);

    trackShareAction('Facebook');
  };

  const onClickShareLinkedIn = () => {
    shareLinkedIn(lang);

    trackShareAction('LinkedIn');
  };

  const onClickShareX = () => {
    if (['article', 'event', 'study', 'idea', 'ideas_request'].includes(postType)) {
      shareX((url: any) => `${postTitle} ${url}`, lang);
    } else {
      shareX((url: any) => url, lang);
    }

    trackShareAction('X');
  };

  const openQRCodeModal = () => {
    Analytics.track('Post', 'Generate QR Code', `Generated QR code for post ${postId}`, {
      'Post ID': postId,
      'Post Type': postType,
    });
    setQrCodeDialogOpen(true);
  };

  const showQRCode = (qrCode = generateQrCode(space, { isBlackAndWhite })) => {
    if (qrCodeRef.current) {
      QRCodeStyling._clearContainer(qrCodeRef.current);
      qrCode.append(qrCodeRef.current);
    }
  };

  useEffect(() => {
    const qrCode = generateQrCode(space, { isBlackAndWhite });
    showQRCode(qrCode);
  }, [isBlackAndWhite]);

  const downloadQRCode = () => {
    const qrCode = generateQrCode(space, { isBlackAndWhite, size: 500 });
    qrCode.download();
  };

  const handleShareMenuClick = (event: any) => {
    setShareMenuAnchorEl(event.currentTarget);
  };

  const handleShareMenuClose = () => {
    setShareMenuAnchorEl(null);
  };

  const renderShareItMenu = () => {
    return (
      <Box
        sx={{
          textAlign: 'right',
          ml: 1,
        }}
      >
        <StyledIconButton
          aria-label="social share menu"
          aria-controls="social-share-menu"
          aria-haspopup="true"
          color="primary"
          onClick={handleShareMenuClick}
        >
          <CustomIcon src={Icons.misc.share} color="primary" />
        </StyledIconButton>

        <Menu
          id="social-share-menu"
          anchorEl={shareMenuAnchorEl}
          open={Boolean(shareMenuAnchorEl)}
          onClose={handleShareMenuClose}
          keepMounted // keepMounted for Accessibility reason
        >
          <MenuItem
            onClick={() => {
              onClickShareFacebook();
              handleShareMenuClose();
            }}
            aria-label="Share on Facebook"
          >
            <ListItemIcon>
              <FacebookIcon style={{ fontSize: 24 }} />
            </ListItemIcon>

            <Typography variant="inherit">Facebook</Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              onClickShareX();
              handleShareMenuClose();
            }}
            aria-label="Share on X (Twitter)"
          >
            <ListItemIcon>
              <XIcon style={{ fontSize: 21 }} />
            </ListItemIcon>

            <Typography variant="inherit">X (Twitter)</Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              onClickShareLinkedIn();
              handleShareMenuClose();
            }}
            aria-label="Share on LinkedIn"
          >
            <ListItemIcon>
              <LinkedInIcon style={{ fontSize: 24 }} />
            </ListItemIcon>

            <Typography variant="inherit">LinkedIn</Typography>
          </MenuItem>

          <MenuItem
            onClick={() => {
              openQRCodeModal();
              handleShareMenuClose();
            }}
            aria-label="QR code"
          >
            <ListItemIcon>
              <QrCode2Icon />
            </ListItemIcon>

            <Typography variant="inherit">
              <FormattedMessage id="POSTS.SHOW.QR_CODE.GENERATE" />
            </Typography>
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  const renderShareItButtons = () => {
    return (
      <Grid
        container
        spacing={2}
        wrap="nowrap"
        sx={{
          justifyContent: 'flex-end',
          alignItems: 'center',
        }}
      >
        <Grid>
          <Typography
            variant="body1"
            color="textSecondary"
            sx={{
              display: 'inline',
              fontWeight: 'medium',
            }}
          >
            <FormattedMessage id="POSTS.SHOW.SHARE_IT" />
          </Typography>
        </Grid>

        <Grid>
          <StyledIconButton
            color="primary"
            onClick={onClickShareFacebook}
            aria-label="Share on Facebook"
          >
            <FacebookIcon style={{ fontSize: 24 }} />
          </StyledIconButton>
        </Grid>

        <Grid>
          <StyledIconButton
            color="primary"
            onClick={onClickShareX}
            aria-label="Share on X (Twitter)"
          >
            <XIcon style={{ fontSize: 21 }} />
          </StyledIconButton>
        </Grid>
        <Grid>
          <StyledIconButton
            color="primary"
            onClick={onClickShareLinkedIn}
            aria-label="Share on LinkedIn"
          >
            <LinkedInIcon style={{ fontSize: 24 }} />
          </StyledIconButton>
        </Grid>
        <Grid>
          <StyledIconButton
            color="primary"
            onClick={openQRCodeModal}
            aria-label={intl.formatMessage({ id: 'POSTS.SHOW.QR_CODE.GENERATE' })}
          >
            <QrCode2Icon />
          </StyledIconButton>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box>
      <Dialog
        id="qr-code-dialog"
        label="qr-code-dialog"
        open={qrCodeDialogOpen}
        title={intl.formatMessage({ id: 'POSTS.SHOW.QR_CODE.TITLE' })}
        onClose={() => setQrCodeDialogOpen(false)}
        dismissible
        actions={
          <Button variant="contained" onClick={downloadQRCode}>
            <FormattedMessage id="POSTS.SHOW.QR_CODE.DOWNLOAD" />
          </Button>
        }
      >
        <Box
          sx={{ textAlign: 'center' }}
          ref={(element: any) => {
            if (qrCodeRef.current !== element) {
              qrCodeRef.current = element;
              showQRCode();
            }
          }}
        />
        <br />
        <FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={isBlackAndWhite}
                onChange={() => setIsBlackAndWhite(!isBlackAndWhite)}
              />
            }
            label={intl.formatMessage({ id: 'POSTS.SHOW.QR_CODE.BLACK_AND_WHITE' })}
          />
        </FormControl>
      </Dialog>
      {isSmallScreenSize ? renderShareItMenu() : renderShareItButtons()}
    </Box>
  );
}

export default ShareIt;
